import React from "react";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { offersPageMarkOfferAsErrorDialogState } from "store/OffersPageStore";
import { useMarkOfferAsErrorMutation } from "state/queries/useOffers";
import { useLoadOffers } from "store/ProposalsPageHooks";

// Confirmation of marking an offer as error entry, or of unmarking it (admins only)
export const MarkOfferAsErrorDialog: React.FC = () => {
  const { t } = useTranslation();
  const loadOffers = useLoadOffers();
  const [dialogState, setDialogState] = useAtom(
    offersPageMarkOfferAsErrorDialogState
  );
  const { markAsError } = dialogState;

  const handleClose = () => {
    setDialogState({ isOpen: false, offer: null, markAsError });
  };

  const mutation = useMarkOfferAsErrorMutation(() => {
    handleClose();
    loadOffers({
      skip: 0,
      top: 40,
      reset: true,
    });
  });

  const handleConfirm = () => {
    if (dialogState.offer?.OfferId) {
      mutation.mutate({ offerId: dialogState.offer.OfferId, markAsError });
    }
  };

  return (
    <Dialog
      open={dialogState.isOpen}
      onOpenChange={(e, data) => !data.open && handleClose()}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={handleClose}
                disabled={mutation.isPending}
              />
            }
          >
            {markAsError
              ? t("qtg.markOfferAsError.dialog.title", "Mark as Error")
              : t("qtg.unmarkOfferAsError.dialog.title", "Unmark Error")}
          </DialogTitle>
          <DialogContent>
            <p>
              {markAsError
                ? t("qtg.markOfferAsError.dialog.message", {
                    offerId: dialogState.offer?.OfferId,
                  })
                : t("qtg.unmarkOfferAsError.dialog.message", {
                    offerId: dialogState.offer?.OfferId,
                  })}
            </p>
            <p style={{ marginTop: "1rem" }}>
              {markAsError
                ? t("qtg.markOfferAsError.dialog.confirmation")
                : t("qtg.unmarkOfferAsError.dialog.confirmation")}
            </p>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={handleClose}
              disabled={mutation.isPending}
            >
              {t("qtg.common.cancel")}
            </Button>
            <Button
              data-help="offers-mark-as-error-confirm"
              appearance="primary"
              onClick={handleConfirm}
              disabled={mutation.isPending}
            >
              {mutation.isPending
                ? t("qtg.common.processing")
                : markAsError
                  ? t("qtg.markOfferAsError.dialog.confirm", "Mark as Error")
                  : t("qtg.unmarkOfferAsError.dialog.confirm", "Unmark")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
